namespace $ {
	/** UI commands run once at the MCP async boundary, not during reactive render. */
	export class $shm_hitalama_components_ui extends $mol_object {
		app() { return this.$.$shm_hitalama_app.Root( 0 ) as $.$$.$shm_hitalama_app }
		@ $mol_mem
		capture() { return new this.$.$shm_hitalama_components_capture }
		private refs = new Map<string, WeakRef<HTMLElement>>()
		private ids = new WeakMap<HTMLElement, string>()
		private sequence = 0
		private running = false
		private lifetime = new AbortController
		private errors: string[] = []
		private on_error = ( event: ErrorEvent )=> { this.errors.push( String( event.message ).slice( 0, 1000 ) ); this.errors = this.errors.slice( -20 ) }
		private on_rejection = ( event: PromiseRejectionEvent )=> { this.errors.push( String( event.reason ).slice( 0, 1000 ) ); this.errors = this.errors.slice( -20 ) }
		private listening = false
		destructor() {
			this.lifetime.abort()
			window.removeEventListener( 'error', this.on_error )
			window.removeEventListener( 'unhandledrejection', this.on_rejection )
			this.refs.clear()
			super.destructor()
		}
		listen() {
			if( this.listening ) return
			this.listening = true
			window.addEventListener( 'error', this.on_error )
			window.addEventListener( 'unhandledrejection', this.on_rejection )
		}
		visible( node: HTMLElement ) {
			return node.isConnected && !!node.getClientRects().length && getComputedStyle( node ).visibility !== 'hidden' && !node.closest( '[hidden],[inert]' )
		}
		async wait( name: string, predicate: ()=> boolean, timeout = 5000 ) {
			const end = performance.now() + Math.min( 10000, Math.max( 100, timeout ) )
			return new Promise<void>( ( resolve, reject )=> {
				let frame = 0
				const timer = setTimeout( ()=> finish( new Error( 'Истекло ожидание: ' + name ) ), Math.max( 100, end - performance.now() ) )
				const observer = new MutationObserver( check )
				const abort = ()=> finish( new Error( 'Клиент закрыт.' ) )
				const finish = ( error?: Error )=> { clearTimeout( timer ); cancelAnimationFrame( frame ); observer.disconnect(); this.lifetime.signal.removeEventListener( 'abort', abort ); error ? reject( error ) : resolve() }
				const self = this
				function check() {
					try { self.guard( name ); if( predicate() ) return finish() } catch( error ) { return finish( error as Error ) }
					cancelAnimationFrame( frame ); frame = requestAnimationFrame( check )
				}
				observer.observe( document.body, { childList: true, subtree: true, attributes: true, characterData: true } )
				this.lifetime.signal.addEventListener( 'abort', abort, { once: true } )
				check()
			})
		}
		guard( name: string ) {
			if( this.lifetime.signal.aborted ) throw new Error( 'Клиент закрыт.' )
			const app = this.app()
			if( app.Components().selected() !== name || this.$.$mol_state_arg.value( 'section' ) !== 'applications' ) throw new Error( 'Открыто другое приложение. Повторите application_open для ' + name )
			if( ![ 'preview', 'code' ].includes( app.Components().application_tab() ) ) throw new Error( 'Превью приложения закрыто. Повторите application_open для ' + name )
		}
		async open( name: string, mode: string ) {
			if( ![ 'demo', 'default' ].includes( mode ) ) throw new Error( 'mode должен быть demo или default.' )
			const definition = await $mol_wire_async( this.$.$shm_hitalama_components_catalog.current()! ).definition( name )
			if( !definition ) throw new Error( 'Модуль не найден.' )
			this.listen(); this.errors = []; this.refs.clear()
			this.$.$mol_state_arg.dict({ ...this.$.$mol_state_arg.dict(), section: 'applications', component: name, project_module: null, application_run: null, application_tab: null, application_version: null, fullscreen_application: null, fullscreen_widget: null })
			const list = this.app().Components()
			// Recreate demo-local state for repeatable saved test runs. Never reset real data.
			list.Demo_model().mocked( mode !== 'demo' )
			list.Demo_model().mocked( mode === 'demo' )
			await this.wait( name, ()=> !!document.querySelector( '[shm_hitalama_components_preview_result]' ) )
			await $mol_wire_async( list.Demo_model() ).result()
			await this.wait( name, ()=> !this.root( name, 'preview' ).querySelector( '[mol_view_error="Promise"],[mol_view_error="$mol_promise_blocker"]' ) )
			return this.inspect( name )
		}
		root( name: string, surface = 'preview' ) {
			this.guard( name )
			if( surface !== 'preview' ) throw new Error( 'surface должен быть preview.' )
			const selector = '[shm_hitalama_components_preview_result]'
			const root = document.querySelector<HTMLElement>( selector )
			if( !root || !this.visible( root ) ) throw new Error( 'Область приложения не открыта: ' + surface )
			return root
		}
		role( node: HTMLElement ) {
			return node.getAttribute( 'role' ) || ({ BUTTON: 'button', TEXTAREA: 'textbox', SELECT: 'combobox', A: 'link', INPUT: node.getAttribute( 'type' ) === 'checkbox' ? 'checkbox' : 'textbox' } as Record<string,string>)[ node.tagName ] || ''
		}
		label( node: HTMLElement ) {
			const labelled = node.getAttribute( 'aria-labelledby' )?.split( /\s+/ ).map( id => document.getElementById( id )?.textContent ?? '' ).join( ' ' ).trim()
			const labels = ( node as HTMLInputElement ).labels
			return ( node.getAttribute( 'aria-label' ) || labelled || ( labels && [...labels].map( item => item.textContent ).join( ' ' ).trim() ) || node.closest( '[mol_labeler]' )?.querySelector( '[mol_labeler_title]' )?.textContent || node.getAttribute( 'title' ) || node.textContent || node.getAttribute( 'placeholder' ) || '' ).trim().slice( 0, 300 )
		}
		controls( root: HTMLElement ) {
			return [...root.querySelectorAll<HTMLElement>( 'button,input,textarea,select,a,[role="button"],[role="checkbox"],[role="textbox"],[contenteditable="true"]' )].filter( node => this.visible( node ) )
		}
		inspect( name: string, surface = 'preview' ) {
			const root = this.root( name, surface )
			for( const [ ref, pointer ] of this.refs ) if( !pointer.deref()?.isConnected ) this.refs.delete( ref )
			const all = this.controls( root ), controls = all.slice( 0, 200 ).map( node => {
				let ref = this.ids.get( node )
				if( !ref ) { ref = 'e' + ++this.sequence; this.ids.set( node, ref ) }
				this.refs.set( ref, new WeakRef( node ) )
				const rect = node.getBoundingClientRect(), input = node as HTMLInputElement
				return { ref, role: this.role( node ), name: this.label( node ), value: input.type === 'password' ? '[redacted]' : String( input.value ?? '' ).slice( 0, 1000 ), disabled: input.disabled || node.getAttribute( 'aria-disabled' ) === 'true', checked: node.getAttribute( 'aria-checked' ) ?? ( input.type === 'checkbox' ? input.checked : undefined ), rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height } }
			})
			const errors = [...root.querySelectorAll<HTMLElement>( '[mol_view_error]' )].map( node => ({ type: node.getAttribute( 'mol_view_error' ), text: node.textContent?.slice( 0, 1000 ) }))
			return { name, surface, text: root.innerText.slice( 0, 16000 ), controls, truncated: all.length > 200, errors, runtime_errors: [...this.errors], synthetic_actions: true }
		}
		target( root: HTMLElement, params: Record<string,any>, waiting = false ) {
			if( params.ref ) {
				const node = this.refs.get( params.ref )?.deref()
				if( !node || !root.contains( node ) || !this.visible( node ) ) throw new Error( 'Элемент устарел или вне области приложения. Перечитайте application_ui.' )
				return node
			}
			let candidates = params.selector ? [...root.querySelectorAll<HTMLElement>( params.selector )] : this.controls( root )
			candidates = candidates.filter( node => this.visible( node ) && ( !params.role || this.role( node ) === params.role ) && ( params.label === undefined && params.target_name === undefined || this.label( node ) === ( params.label ?? params.target_name ) ) )
			if( !candidates.length && waiting ) return null
			if( candidates.length !== 1 ) throw new Error( `Нужно выбрать ровно один элемент, найдено: ${ candidates.length }. Уточните selector, role и label.` )
			return candidates[0]
		}
		async act( name: string, params: Record<string,any> ) {
			const root = this.root( name, params.surface )
			if( params.action === 'expect' ) {
				await this.wait( name, ()=> {
					const current = this.root( name, params.surface )
					const element = params.selector || params.ref || params.role || params.label ? this.target( current, params, true ) : current
					if( !element ) return false
					return ( element.innerText || '' ).includes( params.text ) || ( 'value' in element && String( element.value ).includes( params.text ) )
				}, params.timeout_ms )
			} else {
				const node = params.action === 'scroll' && !params.selector && !params.ref && !params.role && !params.label ? root : this.target( root, params )
				if( !node ) throw new Error( 'Элемент не найден.' )
				if( ( node as HTMLInputElement ).disabled || node.getAttribute( 'aria-disabled' ) === 'true' ) throw new Error( 'Элемент отключён.' )
				if( params.action === 'click' ) node.click()
				else if( params.action === 'fill' ) {
					if( !( node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement ) || node instanceof HTMLInputElement && [ 'file', 'password' ].includes( node.type ) ) throw new Error( 'Выбранный элемент нельзя заполнить этим инструментом.' )
					node.focus(); node.value = String( params.value ?? '' )
					node.dispatchEvent( new Event( 'input', { bubbles: true } ) ); node.dispatchEvent( new Event( 'change', { bubbles: true } ) )
				} else if( params.action === 'scroll' ) node.scrollTo({ left: Number( params.x ) || 0, top: Number( params.y ) || 0, behavior: 'instant' })
				else throw new Error( 'Неизвестное UI-действие.' )
			}
			await new Promise<void>( resolve => {
				const timer = setTimeout( done, 100 ), frames: number[] = []
				function done() { clearTimeout( timer ); for( const frame of frames ) cancelAnimationFrame( frame ); resolve() }
				frames.push( requestAnimationFrame( ()=> frames.push( requestAnimationFrame( done ) ) ) )
			})
			return this.inspect( name, params.surface )
		}
		async handle( method: string, params: Record<string,any> ) {
			if( method === 'application_ui' ) return this.inspect( params.name, params.surface )
			if( this.running ) throw new Error( 'Идёт сохранённый сценарий. Дождитесь результата.' )
			if( method === 'application_open' ) return this.open( params.name, params.mode ?? 'demo' )
			if( method === 'application_ui' ) return this.inspect( params.name, params.surface )
			if( method === 'application_ui_action' ) return this.act( params.name, params )
			if( method === 'application_screenshot' ) return this.screenshot( params.name, params.surface )
			throw new Error( 'Неизвестная команда интерфейса.' )
		}
		async screenshot( name: string, surface?: string ) {
			const node = this.root( name, surface )
			const image = await this.capture().image( node )
			this.guard( name )
			if( !node.isConnected ) throw new Error( 'Панель изменилась во время снимка. Повторите снимок.' )
			return image
		}
		async exclusive<T>( task: ()=> Promise<T> ) {
			if( this.running ) throw new Error( 'В этой вкладке уже идёт сценарий.' )
			this.running = true
			try { return await task() } finally { this.running = false }
		}
	}
}
